import { FC, Fragment, useState } from 'react';
import { IconButton } from '@mui/material';
import { Search } from '@mui/icons-material';
import classnames from 'classnames';
import { useUi } from 'hooks/useUi';
import { TopSearchBarInputField } from './TopSearchBarInputField/TopSearchBarInputField';
import styles from './TopSearchBar.module.scss';

interface TopSearchBarProps {
    searchTerm?: string;
    onSearchTermChange?: (term: string) => void;
    onSearch?: (term: string) => void;
    onArrowKey?: (direction: 'up' | 'down') => void;
    /** Callback function to run when user presses the enter key inside the search bar. */
    onEnterKey?: () => void;
    /** Callback function to alert parent component to changes in mobile search state. */
    onMobileSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TopSearchBar: FC<TopSearchBarProps> = ({
    searchTerm,
    onSearchTermChange,
    onSearch,
    onArrowKey,
    onEnterKey,
    onMobileSearchOpen,
}) => {
    const { ui } = useUi();
    const [isMobileSearchOpen, setIsMobileSearchOpen] =
        useState<boolean>(false);

    const searchChanged = (term: string) => {
        if (!!onSearchTermChange) onSearchTermChange(term);
    };

    const openMobileSearch = () => {
        setIsMobileSearchOpen(true);
        onMobileSearchOpen(true);
    };

    const closeMobileSearch = () => {
        setIsMobileSearchOpen(false);
        onMobileSearchOpen(false);
    };

    return (
        <Fragment>
            {ui.isMobile && (
                <div
                    className={classnames(
                        styles.TopSearchBar,
                        styles.mobileSearchBar
                    )}
                    data-testid="TopSearchBar"
                >
                    {!isMobileSearchOpen && (
                        <IconButton
                            onClick={openMobileSearch}
                            sx={{ maxWidth: '44px' }}
                            size="large"
                        >
                            <Search sx={{ color: '#fafafa' }} />
                        </IconButton>
                    )}
                    {isMobileSearchOpen && (
                        <TopSearchBarInputField
                            searchTerm={searchTerm}
                            onSearchTermChange={searchChanged}
                            onSearch={onSearch}
                            onBlur={closeMobileSearch}
                        />
                    )}
                </div>
            )}
            {!ui.isMobile && (
                <div className={styles.TopSearchBar} data-testid="TopSearchBar">
                    <TopSearchBarInputField
                        searchTerm={searchTerm}
                        onSearchTermChange={searchChanged}
                        onSearch={onSearch}
                        onArrowKey={onArrowKey}
                        onEnterKey={onEnterKey}
                    />
                </div>
            )}
        </Fragment>
    );
};
